import { buildCustomRangePayload, formatNative, formatUsd, getDefaultCustomRange } from './optionsDashboardUtils';
import { buildMoneyValuePresentation, getPnlToneClass } from './optionsVisualConfig';
import { buildStrategyFilterOptions, formatStrategyLabel } from './optionsStrategyFilterUtils';
import { formatLocalizedNumber } from './optionsLocaleFormatting';

export const HISTORY_VIEWS = ['operations', 'campaigns', 'rolls'];
export const HISTORY_CURRENCIES = ['usd', 'btc', 'eth'];
export const HISTORY_RANGES = ['30d', '90d', 'ytd', 'all', 'custom'];
export const HISTORY_UNDERLYINGS = ['all', 'btc', 'eth'];
export const HISTORY_STATUSES = ['all', 'OPEN', 'CLOSED'];

const DATE_MONTHS = {
  en: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
  pt: ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'],
};

export const formatDate = (isoString, language = 'en') => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  const months = DATE_MONTHS[language] || DATE_MONTHS.en;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = months[date.getUTCMonth()] || DATE_MONTHS.en[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
};

export const formatDateTime = (isoString, language = 'en') => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  const months = DATE_MONTHS[language] || DATE_MONTHS.en;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = months[date.getUTCMonth()] || DATE_MONTHS.en[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} · ${hours}:${minutes}`;
};

export const formatCampaignPeriodLabel = (isoString, language = 'en') => {
  if (!isoString) return '—';
  const date = new Date(isoString);
  const months = DATE_MONTHS[language] || DATE_MONTHS.en;
  const month = months[date.getUTCMonth()] || DATE_MONTHS.en[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${month} ${year}`;
};

export const formatCompactPnl = ({ selectedCurrency, nativeValue, usdValue, currency }) => {
  const presentation = buildMoneyValuePresentation({
    selectedCurrency,
    nativeValue,
    usdValue,
    underlying: currency,
    digits: 4,
    tone: 'pnl',
  });
  return {
    primary: presentation.primary,
    secondary: presentation.secondary,
    sign: Number(selectedCurrency === 'usd' ? (usdValue || 0) : (nativeValue || 0)),
  };
};

export const getPnlClassName = (value) => getPnlToneClass(value);

export const getRangeConfig = (selectedRange, appliedCustomRange) => {
  if (selectedRange === 'custom') {
    return {
      selectedRange,
      ...buildCustomRangePayload(appliedCustomRange),
    };
  }
  return {
    selectedRange,
    start: null,
    end: null,
  };
};

export const defaultCustomRange = getDefaultCustomRange();

export const getEffectiveUnderlyingFilter = (_selectedCurrency, underlyingFilter) => underlyingFilter;

export const HISTORY_SORT_MODES = ['recent', 'biggest_win', 'biggest_loss'];

export const formatHistorySortLabel = (mode, t) => {
  if (mode === 'biggest_win') return t('history.sortBiggestWin');
  if (mode === 'biggest_loss') return t('history.sortBiggestLoss');
  return t('history.sortNewest');
};

export const getOperationNetPnlUsd = (operation) => Number(operation?.net_pnl_usd ?? operation?.net_pnl_base ?? 0);

export const sortOperationsByMode = (operations = [], sortMode = 'recent') => {
  if (sortMode === 'biggest_win') {
    return [...operations].sort((a, b) => getOperationNetPnlUsd(b) - getOperationNetPnlUsd(a));
  }
  if (sortMode === 'biggest_loss') {
    return [...operations].sort((a, b) => getOperationNetPnlUsd(a) - getOperationNetPnlUsd(b));
  }
  return operations;
};

export const formatHistoryStatus = (statusValue, t) => {
  const normalized = String(statusValue || '').toUpperCase();
  // PARTIAL operations still have live exposure, so visually they are treated as OPEN.
  if (normalized === 'OPEN' || normalized === 'PARTIAL') return t('history.statuses.open');
  if (normalized === 'CLOSED') return t('history.statuses.closed');
  if (normalized === 'ALL') return t('history.statuses.all');
  return statusValue || '—';
};

export const formatHistoryRollLabel = (t) => t('history.rollBadge');

export const toIsoRangeBounds = ({ selectedRange, appliedCustomRange }) => {
  if (selectedRange === 'all') {
    return { start: null, end: null };
  }

  if (selectedRange === 'custom') {
    return buildCustomRangePayload(appliedCustomRange);
  }

  const end = new Date();
  const start = new Date(end);
  if (selectedRange === '30d') start.setUTCDate(start.getUTCDate() - 30);
  if (selectedRange === '90d') start.setUTCDate(start.getUTCDate() - 90);
  if (selectedRange === 'ytd') {
    start.setUTCMonth(0, 1);
    start.setUTCHours(0, 0, 0, 0);
  }

  return {
    start: `${start.toISOString().slice(0, 10)}T00:00:00+00:00`,
    end: `${end.toISOString().slice(0, 10)}T23:59:59+00:00`,
  };
};

export const isTimestampInRange = (isoString, bounds) => {
  if (!isoString) return false;
  if (!bounds.start && !bounds.end) return true;
  const target = new Date(isoString).getTime();
  const start = bounds.start ? new Date(bounds.start).getTime() : Number.NEGATIVE_INFINITY;
  const end = bounds.end ? new Date(bounds.end).getTime() : Number.POSITIVE_INFINITY;
  return target >= start && target <= end;
};

export const operationMatchesRange = (operation, bounds) => {
  if (!bounds.start && !bounds.end) return true;
  return isTimestampInRange(operation.opened_at, bounds) || isTimestampInRange(operation.closed_at, bounds);
};

export const campaignMatchesRange = (campaign, bounds) => {
  if (!bounds.start && !bounds.end) return true;
  return isTimestampInRange(campaign.opened_at, bounds);
};

export const normalizeSizeDisplay = (legs = []) => {
  if (!legs.length) return '—';
  const quantities = legs.map((leg) => Number(leg.quantity_opened ?? leg.quantity_remaining ?? 0));
  const min = Math.min(...quantities);
  const max = Math.max(...quantities);
  if (Math.abs(max - min) < 0.000001) {
    return `${formatLocalizedNumber(min, { minimumFractionDigits: min % 1 === 0 ? 0 : 2, maximumFractionDigits: 4 })} contracts`;
  }
  return `${formatLocalizedNumber(min, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}–${formatLocalizedNumber(max, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} contracts`;
};

export const sortOperationsNewestFirst = (operations = []) => {
  return [...operations].sort((a, b) => {
    const aDate = new Date(a.closed_at || a.opened_at || 0).getTime();
    const bDate = new Date(b.closed_at || b.opened_at || 0).getTime();
    return bDate - aDate;
  });
};

export const sortCampaignsNewestFirst = (campaigns = []) => {
  return [...campaigns].sort((a, b) => new Date(b.opened_at || 0).getTime() - new Date(a.opened_at || 0).getTime());
};

export const sortLegsByStrike = (legs = []) => {
  return [...legs].sort((a, b) => Number(a.strike || 0) - Number(b.strike || 0));
};

export const buildGroupedRollEvents = (campaignDetails = []) => {
  const events = [];

  campaignDetails.forEach((detail) => {
    const campaign = detail.campaign;
    const operationsById = new Map((detail.operations || []).map((operation) => [operation.id, operation]));
    const legsById = new Map((detail.legs || []).map((leg) => [leg.id, leg]));
    const grouped = new Map();

    (detail.roll_links || []).forEach((link) => {
      const sourceLegs = (link.source_leg_ids || []).map((id) => legsById.get(id)).filter(Boolean);
      const targetLegs = (link.target_leg_ids || []).map((id) => legsById.get(id)).filter(Boolean);
      const firstSourceLeg = sourceLegs[0];
      const firstTargetLeg = targetLegs[0];
      const actualRollAt = firstSourceLeg?.closed_at || firstTargetLeg?.opened_at || link.created_at;

      const key = `${link.source_operation_id}__${link.target_operation_id}`;
      const current = grouped.get(key) || {
        key,
        campaign,
        sourceOperation: operationsById.get(link.source_operation_id) || null,
        targetOperation: operationsById.get(link.target_operation_id) || null,
        links: [],
        transitions: [],
        rollTypes: new Set(),
        createdAt: actualRollAt,
      };

      current.links.push(link);
      current.createdAt = current.createdAt && actualRollAt
        ? (new Date(current.createdAt) > new Date(actualRollAt) ? current.createdAt : actualRollAt)
        : (current.createdAt || actualRollAt || null);

      (link.roll_types || []).forEach((type) => current.rollTypes.add(type));

      sourceLegs.forEach((sourceLeg) => {
        const targetLeg = targetLegs.find((candidate) => candidate.option_type === sourceLeg.option_type) || targetLegs[0] || null;
        current.transitions.push({
          optionType: sourceLeg.option_type,
          sourceStrike: sourceLeg.strike,
          targetStrike: targetLeg?.strike || null,
          positionSide: sourceLeg.position_side,
          sourceExpiration: sourceLeg.expiration,
          targetExpiration: targetLeg?.expiration || null,
          sourcePnlBase: sourceLeg.net_pnl_base,
          sourcePnlUsd: sourceLeg.net_pnl_usd,
          rollTypes: link.roll_types || [],
        });
      });

      grouped.set(key, current);
    });

    grouped.forEach((value) => {
      events.push({
        ...value,
        rollTypes: Array.from(value.rollTypes),
      });
    });
  });

  return events.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
};

export const rollEventMatchesRange = (event, bounds) => {
  if (!bounds.start && !bounds.end) return true;
  return isTimestampInRange(event.createdAt, bounds);
};

export const strategyMatches = (strategyValue, strategyFilter) => strategyFilter === 'all' || strategyValue === strategyFilter;
// PARTIAL operations still hold live exposure, so the ABERTA (OPEN) filter matches them too.
export const statusMatches = (statusValue, statusFilter) => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'OPEN') return statusValue === 'OPEN' || statusValue === 'PARTIAL';
  return statusValue === statusFilter;
};
export const underlyingMatches = (underlyingValue, effectiveUnderlying) => effectiveUnderlying === 'all' || underlyingValue?.toLowerCase() === effectiveUnderlying;
export const buildHistoryStrategyOptions = (strategyValues = []) => buildStrategyFilterOptions(strategyValues);
export { formatStrategyLabel };
